import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import Phone from "./Phone";
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { useMemo, useCallback, useState, useEffect } from 'react';

interface PhoneControlProps {
    data: any;
    handleChange(path: string, value: string): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const PhoneControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: PhoneControlProps) => {
    const [countryData, setCountryData] = useState({
        countryCallingCode: '',
        countryCode: "",
        nationalNumber: "",
        numberValue: ""
    });

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Phone, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Phone, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Update country data when value changes
    useEffect(() => {
        if (isValidPhoneNumber(value) && countryData.countryCode === "") {
            const phoneNumber = parsePhoneNumber(value);
            if (phoneNumber) {
                setCountryData({
                    countryCallingCode: phoneNumber.countryCallingCode || '',
                    countryCode: phoneNumber.country || '',
                    nationalNumber: phoneNumber.nationalNumber || '',
                    numberValue: phoneNumber.number || '',
                });
            }
        }
    }, [value, countryData.countryCode]); // Only run when value changes

    // Memoize the change handler
    const handlePhoneChange = useCallback((val: string, data: any) => {
        let processedValue = val;
        if (val === `+${data.countryCallingCode}`) {
            processedValue = "";
        }
        setCountryData(data);
        handleChange(path, processedValue);
    }, [handleChange, path]);

    // Memoize error message
    const phoneError = useMemo(() => {
        if (!isValidPhoneNumber(value) && enabled && value) {
            return "Please enter a valid mobile number";
        }
        return errors;
    }, [errors, value, enabled]);

    // Memoize the Phone component props
    const phoneProps = useMemo(() => ({
        variant: 'outlined' as const,
        error: phoneError,
        value: value || '',
        required: required,
        label: uischema.label,
        description: schema.description,
        onChange: handlePhoneChange,
        enabled: enabled,
        visible: visible,
        options: uischema.options,
        appValidated: appValidated
    }), [
        phoneError,
        value,
        required,
        uischema.label,
        schema.description,
        handlePhoneChange,
        enabled,
        visible,
        uischema.options,
        appValidated
    ]);

    return <Phone {...phoneProps} />;
};

export default React.memo(withJsonFormsControlProps(PhoneControl));
